import { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { Spinner } from "./ui";
import { hasCompletedOnboarding, isAdmin } from "../api/types";
import { BRAND } from "../brand";
import Icon from "./Icon";

/**
 * Ported from the C# _Layout.cshtml
 * (src/LoadAndFlow.Web.LoadAndFlow/Views/Shared/_Layout.cshtml). Class names
 * are copied from that markup rather than rewritten, so the two render alike.
 *
 * Links that existed only for anonymous marketing pages (/features, /pricing,
 * /auth/register) are omitted: this shell only ever renders for signed-in
 * users, where the C# layout did not show them either.
 */

const NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: "home", end: true, testid: "nav-dashboard-link" },
  { to: "/exercises", label: "Exercises", icon: "book-open", end: false, testid: "nav-exercises-link" },
  { to: "/sessions", label: "History", icon: "clock", end: false, testid: "nav-sessions-link" },
  { to: "/settings", label: "Settings", icon: "settings", end: false, testid: "nav-settings-link" },
] as const;

const ACTIVE_DESKTOP = "text-gray-900 dark:text-textDark-primary bg-gray-100 dark:bg-dark-elevated";
const INACTIVE_DESKTOP =
  "text-gray-500 dark:text-textDark-secondary hover:text-gray-700 dark:hover:text-textDark-primary hover:bg-gray-50 dark:hover:bg-dark-elevated";
const INACTIVE_MOBILE =
  "text-gray-600 dark:text-textDark-secondary hover:bg-gray-50 dark:hover:bg-dark-elevated";

function desktopClass({ isActive }: { isActive: boolean }): string {
  return `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
    isActive ? ACTIVE_DESKTOP : INACTIVE_DESKTOP
  }`;
}

function mobileClass({ isActive }: { isActive: boolean }): string {
  return `flex items-center gap-2 px-3 py-2.5 min-h-[44px] rounded-lg text-sm font-medium transition-colors ${
    isActive ? ACTIVE_DESKTOP : INACTIVE_MOBILE
  }`;
}

const FOOTER_LINK =
  "text-sm text-gray-400 dark:text-textDark-muted hover:text-gray-600 dark:hover:text-textDark-secondary transition-colors";

/**
 * The C# layout set the dark class from an inline script before paint, reading
 * the same `theme` key. Keeping the key means a user's existing preference
 * carries over rather than silently resetting.
 */
function useDarkMode(): [boolean, () => void] {
  const [dark, setDark] = useState(
    () => typeof localStorage !== "undefined" && localStorage.getItem("theme") === "dark",
  );

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  return [dark, () => setDark((d) => !d)];
}

export default function AppLayout() {
  const { signedIn, loading, user, signOut } = useAuth();
  const location = useLocation();
  const [dark, toggleDark] = useDarkMode();
  const [menuOpen, setMenuOpen] = useState(false);

  // Any navigation should dismiss the mobile menu, or it stays open over the
  // page the user just moved to.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Waiting on the initial profile load — redirecting here would bounce a
  // returning user to the login page on every refresh.
  if (loading) return <Spinner label="Loading your account" />;

  if (!signedIn) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  // Onboarding records an explicit health acknowledgement before any training
  // is prescribed, so it has to gate the app rather than merely be offered.
  if (user && !hasCompletedOnboarding(user) && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  const navItems = isAdmin(user)
    ? [
        ...NAV_ITEMS,
        {
          to: "/admin",
          label: "Admin",
          icon: "shield-check",
          end: false,
          testid: "nav-admin-link",
        } as const,
      ]
    : NAV_ITEMS;

  return (
    <div className="bg-gray-50 dark:bg-dark-background min-h-screen flex flex-col font-sans text-gray-900 dark:text-textDark-primary antialiased">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:text-gray-900 focus:rounded-lg focus:shadow-lg focus:ring-2 focus:ring-emerald-500 dark:focus:bg-dark-surface dark:focus:text-textDark-primary"
        data-testid="skip-to-content-link"
      >
        Skip to content
      </a>

      <nav
        className="sticky top-0 z-50 bg-white/80 dark:bg-dark-surface/80 backdrop-blur-lg border-b border-gray-200 dark:border-dark-border"
        data-testid="nav-bar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
              <img src={BRAND.logoPath} alt={BRAND.name} className="h-9 w-9" />
              <span className="text-lg leading-none">
                <span className="font-semibold tracking-tight text-gray-900 dark:text-textDark-primary">
                  {BRAND.name}
                </span>
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={desktopClass}
                  data-testid={item.testid}
                >
                  <Icon name={item.icon} size="sm" />
                  {item.label}
                </NavLink>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <button
                data-testid="nav-dark-mode-toggle"
                onClick={toggleDark}
                aria-label="Toggle dark mode"
                aria-pressed={dark}
                className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:text-textDark-muted dark:hover:text-textDark-secondary hover:bg-gray-100 dark:hover:bg-dark-elevated transition-colors"
              >
                <Icon name="moon" size="md" />
              </button>

              <div className="hidden md:flex items-baseline gap-3">
                <span
                  data-testid="nav-user-display"
                  className="text-sm text-gray-600 dark:text-textDark-secondary"
                >
                  {user?.email}
                </span>
                <button
                  onClick={() => void signOut()}
                  data-testid="nav-logout-button"
                  className="text-sm text-gray-500 dark:text-textDark-secondary hover:text-gray-900 dark:hover:text-textDark-primary transition-colors"
                >
                  Sign Out
                </button>
              </div>

              <button
                className="md:hidden p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:text-textDark-muted dark:hover:text-textDark-secondary hover:bg-gray-100 dark:hover:bg-dark-elevated transition-colors"
                data-testid="nav-mobile-menu-button"
                aria-label="Toggle navigation menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((open) => !open)}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {menuOpen && (
          <div
            className="md:hidden border-t border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface"
            data-testid="nav-mobile-menu"
          >
            <div className="px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={mobileClass}
                  data-testid={`${item.testid}-mobile`}
                >
                  <Icon name={item.icon} size="sm" />
                  {item.label}
                </NavLink>
              ))}
              <div className="pt-3 mt-3 border-t border-gray-200 dark:border-dark-border">
                <span className="block px-3 py-1 text-sm text-gray-500 dark:text-textDark-muted">
                  {user?.email}
                </span>
                <button
                  onClick={() => void signOut()}
                  data-testid="nav-logout-button-mobile"
                  className="block w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-textDark-secondary hover:bg-gray-50 dark:hover:bg-dark-elevated"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      <main id="main-content" className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      <footer className="bg-white dark:bg-dark-surface border-t border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={BRAND.logoPath} alt={BRAND.name} className="h-5 w-5 opacity-50" />
              <span className="text-sm text-gray-400 dark:text-textDark-muted">{BRAND.name}</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              <Link to="/exercises" className={FOOTER_LINK}>
                Exercises
              </Link>
              <Link to="/terms" className={FOOTER_LINK} data-testid="footer-terms-link">
                Terms
              </Link>
              <Link to="/privacy" className={FOOTER_LINK} data-testid="footer-privacy-link">
                Privacy
              </Link>
              <Link to="/support" className={FOOTER_LINK} data-testid="footer-support-link">
                Support
              </Link>
            </div>
            <p className="text-xs text-gray-400 dark:text-textDark-muted">
              &copy; 2026 {BRAND.name}
            </p>
          </div>
          <div className="mt-4 md:mt-2 text-center">
            <p className="text-xs text-gray-400 dark:text-textDark-muted leading-relaxed">
              {BRAND.footerDisclaimer}
            </p>
            <p className="text-xs text-gray-400 dark:text-textDark-muted leading-relaxed mt-2.5">
              Always consult a qualified fitness professional before beginning any exercise
              programme.
            </p>
            <p className="text-xs text-gray-400 dark:text-textDark-muted leading-relaxed mt-2.5">
              Also from us:{" "}
              <a
                href={`https://${BRAND.sisterDomain}`}
                className="text-emerald-700 dark:text-emerald-400 hover:underline transition-colors"
                data-testid="footer-sister-link"
              >
                {BRAND.sisterBrandName}
              </a>{" "}
              — {BRAND.sisterTagline}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
